/**
 * ProductListPage.js - 商品列表管理页
 * 支持关键词搜索、状态筛选、分类筛选，提供新建/编辑/删除操作
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Image,
  Popconfirm,
  message,
  Row,
  Col,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { adminProductApi } from '../../api/index';

const { Title } = Typography;
const { Option } = Select;

// 商品状态对应颜色和标签
const statusConfig = {
  active: { color: 'green', label: '上架' },
  draft: { color: 'orange', label: '草稿' },
  archived: { color: 'red', label: '已归档' },
};

export default function ProductListPage() {
  const navigate = useNavigate();

  // 列表数据与分页
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // 搜索与筛选条件
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState(undefined);
  const [categoryId, setCategoryId] = useState(undefined);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // 分类列表（用于筛选下拉）
  const [categories, setCategories] = useState([]);

  // 拉取分类列表
  const fetchCategories = useCallback(async () => {
    try {
      const data = await adminProductApi.listCategories();
      setCategories(Array.isArray(data) ? data : data?.list || []);
    } catch {
      // 分类加载失败不阻塞主流程
    }
  }, []);

  // 拉取商品列表
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminProductApi.list({
        keyword: keyword || undefined,
        status: status || undefined,
        categoryId: categoryId || undefined,
        page,
        pageSize,
      });
      // 兼容分页结构和数组结构
      if (Array.isArray(data)) {
        setProducts(data);
        setTotal(data.length);
      } else {
        setProducts(data?.list || data?.items || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      message.error('加载商品列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [keyword, status, categoryId, page, pageSize]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // 删除商品
  const handleDelete = async (id) => {
    try {
      await adminProductApi.delete(id);
      message.success('商品已删除');
      fetchProducts();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 搜索触发（重置到第一页）
  const handleSearch = () => {
    setPage(1);
    fetchProducts();
  };

  // 表格列配置
  const columns = [
    {
      title: '图片',
      dataIndex: 'images',
      key: 'image',
      width: 70,
      render: (images) => {
        const url = Array.isArray(images) && images[0]?.url;
        return url ? (
          <Image
            src={url}
            width={48}
            height={48}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={false}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              background: '#f5f5f5',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              color: '#bbb',
            }}
          >
            无图
          </div>
        );
      },
    },
    {
      title: '商品名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s) => {
        const cfg = statusConfig[s] || { color: 'default', label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 70,
      align: 'center',
      render: (v) => (v ?? 0),
    },
    {
      title: '规格数',
      dataIndex: 'variants',
      key: 'variantsCount',
      width: 80,
      align: 'center',
      render: (variants) => (Array.isArray(variants) ? variants.length : 0),
    },
    {
      title: '起始价格',
      dataIndex: 'variants',
      key: 'minPrice',
      width: 110,
      align: 'right',
      render: (variants) => {
        if (!Array.isArray(variants) || variants.length === 0) return '-';
        const prices = variants
          .map((v) => Number(v.price))
          .filter((p) => !isNaN(p));
        if (prices.length === 0) return '-';
        return `$${Math.min(...prices).toFixed(2)}`;
      },
    },
    {
      title: '库存',
      dataIndex: 'variants',
      key: 'inventory',
      width: 80,
      align: 'center',
      render: (variants) => {
        if (!Array.isArray(variants)) return 0;
        return variants.reduce((sum, v) => sum + (Number(v.inventoryQty) || 0), 0);
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val) =>
        val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Popconfirm
            title="确认删除该商品？"
            description="删除后数据无法恢复。"
            onConfirm={() => handleDelete(record.id)}
            okText="确认删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          商品管理
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/products/new')}
        >
          新建商品
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }}>
        {/* 搜索筛选栏 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="搜索商品名称/关键词"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="状态筛选"
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">上架</Option>
              <Option value="draft">草稿</Option>
              <Option value="archived">已归档</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="分类筛选"
              value={categoryId}
              onChange={(v) => {
                setCategoryId(v);
                setPage(1);
              }}
              allowClear
              style={{ width: '100%' }}
            >
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={2}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              block
            >
              搜索
            </Button>
          </Col>
        </Row>

        {/* 商品表格 */}
        <Table
          dataSource={products}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/products/${record.id}/edit`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: '暂无商品数据' }}
        />
      </Card>
    </div>
  );
}
